import type { Result, ResultAsync } from 'neverthrow';
import { err, ok } from 'neverthrow';
import { type BackoffFn, RECOMMENDED_BACKOFF_FN } from './backoff';
import { type ConnectError } from '@connectrpc/connect';
import { canHttpRetry, canRpcRetry, fetchErrorShouldRetry } from './retry-helpers';
import type { OperationError, OperationResult } from './utils';
import { compact } from 'lodash';

export class RetryResponseError<V> extends Error {
  constructor(public readonly lastResponse: V) {
    super('max tries exceeded');
  }

  readonly result = this.lastResponse;

  readonly isValueError = true;

  readonly isThrownError = false;
}

export class RetryThrownError extends Error {
  constructor(
    public readonly lastFailure: unknown,
    public readonly lastState: { attemptsSoFar: number; maxTries: number; shouldRetry: boolean },
  ) {
    const message = compact([
      lastState.attemptsSoFar >= lastState.maxTries && 'max tries exceeded',
      !lastState.shouldRetry && 'shouldRetry returned false',
    ]).join(' AND ');
    super(message, { cause: lastFailure });
  }

  readonly result = this.lastFailure;

  readonly isValueError = false;

  readonly isThrownError = true;
}

export const DEFAULT_MAX_TRIES = 3;

export type RetryOptions<V, E> = {
  maxTries?: number;
  delay?: number | BackoffFn;
  /**
   * Invoked on every error. attemptsSoFar includes the most recent attempt that caused this error.
   */
  onEachError?: (error: unknown, attemptsSoFar: number) => void;
  /**
   * Invoked after each successful response. attemptsSoFar includes the most recent attempt that caused this response.
   * Return true to retry, false to stop retrying.
   * Default is to never try for a valid response.
   * MaxTries will still be enforced.
   */
  shouldRetryOnResponse?: (response: V, attemptsSoFar: number) => boolean;
  /**
   * Invoked after each error. attemptsSoFar includes the most recent attempt that caused this error.
   * Return true to retry, false to stop retrying.
   * Default is to retry on all errors.
   * MaxTries will still be enforced.
   */
  shouldRetryOnError?: (error: E, attemptsSoFar: number) => boolean;
};

/**
 * Retries an RPC until it succeeds or maxTries is reached.
 *
 * @see {@link retryAsync} for more information and WARNINGs.
 */
export const retryRpc = <V, E extends ConnectError>(
  fn: () => ResultAsync<V, E>,
  options?: RetryOptions<V, E>,
): Promise<Result<V, RetryResponseError<V> | RetryThrownError>> => {
  return retryAsync<V, E>(
    async () => {
      const result = await fn();
      return result.isOk() ? result.value : Promise.reject(result.error);
    },
    {
      shouldRetryOnError: (error) => canRpcRetry(error.code),
      ...options,
    },
  );
};

/**
 * Retry an auto-generated API fetch until it succeeds or maxTries is reached.
 *
 * @see {@link retryAsync} for more information and WARNINGs.
 */
export const retryApi = <OP_RESULT extends OperationResult<unknown>>(
  apiFn: () => Promise<OP_RESULT>,
  options?: RetryOptions<OP_RESULT, OperationResult<OperationError>>,
): Promise<Result<OP_RESULT, RetryResponseError<OP_RESULT> | RetryThrownError>> => {
  return retryAsync(apiFn, {
    shouldRetryOnResponse: (result) => canHttpRetry(result.status),
    shouldRetryOnError: fetchErrorShouldRetry,
    ...options,
  });
};

/**
 * Retry a fetch request until it succeeds or maxTries is reached.
 *
 * @see {@link retryAsync} for more information and WARNINGs.
 */
export const retryFetch = (
  fn: () => Promise<Response>,
  options?: RetryOptions<Response, unknown>,
): Promise<Result<Response, RetryResponseError<Response> | RetryThrownError>> => {
  return retryAsync<Response>(fn, {
    shouldRetryOnResponse: (response) => canHttpRetry(response.status),
    shouldRetryOnError: fetchErrorShouldRetry,
    ...options,
  });
};

/**
 * Retries an async function until it succeeds or maxTries is reached.
 *
 * Not frequently necessary to use this function, because fetchs should use {@link retryFetch}, RPCs should use {@link retryRpc}, and wrappers around
 *
 * WARNING 1: useQuery automatically retries, so you do not need to use this on top of useQuery
 *
 * WARNING 2: you should be sure that the function or API is safe to retry.
 *   For example, a POST request that creates a new user should not be retried, while a GET request for feature flags should be safe to retry.
 */
export const retryAsync = async <VALUE, ERR = unknown>(
  fn: () => Promise<VALUE>,
  options?: RetryOptions<VALUE, ERR>,
): Promise<Result<VALUE, RetryResponseError<VALUE> | RetryThrownError>> => {
  const {
    maxTries = DEFAULT_MAX_TRIES,
    delay = RECOMMENDED_BACKOFF_FN,
    onEachError,
    shouldRetryOnResponse = () => false,
    shouldRetryOnError = () => true,
  } = options || {};

  let attemptsSoFar = 0;
  do {
    const delayMs = typeof delay === 'function' ? delay(attemptsSoFar) : delay;
    if (delayMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, delayMs);
      });
    }
    attemptsSoFar++;

    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await fn();
      const shouldRetry = shouldRetryOnResponse(result, attemptsSoFar);
      if (!shouldRetry) {
        return ok(result);
      }
      if (attemptsSoFar >= maxTries) {
        return err(new RetryResponseError(result));
      }
    } catch (error) {
      onEachError?.(error, attemptsSoFar);
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const shouldRetry = shouldRetryOnError(error, attemptsSoFar);

      if (!shouldRetry || attemptsSoFar >= maxTries) {
        return err(new RetryThrownError(error, { attemptsSoFar, maxTries, shouldRetry }));
      }
    }

    // eslint-disable-next-line no-constant-condition
  } while (true);
};
